import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LegacyReceipt } from '@anwb/poncho/components/receipt';
import { ReceiptProduct } from '@anwb/poncho/components/receipt/legacy';
import Spinner from '@anwb/poncho/components/spinner';
import Tooltip from '@anwb/poncho/components/tooltip';
import Typography from '@anwb/poncho/components/typography';
import { formatPrice } from '@anwb/poncho/utilities/formatters';
import { setChosenLeaseCarTotalPrice } from 'store/actions';
import { trackEvent } from 'utils/trackEvent';

import {
	basePriceSelector,
	chosenColorSelector,
	chosenDurationPriceSelector,
	chosenDurationSelector,
	chosenKilometrageSelector,
	chosenOptionsSelector,
	chosenOrderCodeSelector,
	chosenPricePointPriceSelector,
	chosenServicesSelector,
	chosenUpgradeSelector,
	defaultUpgradeSelector,
	imageUrlSelector,
	isFlexSelector,
	isNewCarSelector,
	leaseCarDataNameSelector,
	leaseCarGroupedPricePointsSelector,
	paymentInAdvanceAmountSelector,
	paymentInAdvanceChoiceSelector,
	paymentInAdvanceValidSelector,
	plCustomerSelector,
} from '../../store/selectors';
import { GearType, GearTypeKey } from '../../types';
import { formatKilometrageDescription, getMonthlyPriceReduction } from '../../utils';

import { AyvensImage } from './components/AyvensImage/AyvensImage';
import { GlobalReceiptStyle, ReceiptContainer, ReceiptFooter } from './receipt.styled';
import { getSubProductsTotalPrice } from './utils/getSubProductsTotalPrice';
import { useImageLoader } from './utils/useImageLoader';

const RECEIPT_USPS = [
	{
		usps: [
			{
				label: 'Inclusief Wegenwacht Europa Standaard t.w.v. € 163,15 per jaar',
				tooltip: (
					<Tooltip
						dataTest="inclusief-wegenwacht-europa-standaard"
						inline
						name="brandstof"
						customTaggingEvent={(_?: string, isOpen?: boolean) => {
							if (isOpen) {
								trackEvent({
									event: 'user_interaction',
									type: 'form',
									component: 'tooltip-receipt',
									field: 'inclusief-wegenwacht-europa-standaard',
									action: 'click',
									actionValue: 'open',
								});
							}
						}}
					>
						Heb je al Wegenwacht Service? Dan keren wij het reeds betaalde bedrag naar rato aan je
						uit en staat gedurende het leasecontract Wegenwacht Europa Standaard als korting op je
						factuur. Het ANWB-lidmaatschap à € 17,- per jaar en de eventuele meerprijs voor
						uitbreidingen zijn wel voor eigen rekening.
					</Tooltip>
				),
			},
			{
				label: 'Behoud van je opgebouwde schadevrije jaren',
			},
			{
				label: 'Standaard met schadeverzekering inzittenden',
			},
		],
	},
];

export const Receipt = () => {
	const { isMember: isVerifiedMember } = useSelector(plCustomerSelector);
	const basePrice = useSelector(basePriceSelector);
	const selectedUpgrade = useSelector(chosenUpgradeSelector);
	const selectedOptions = useSelector(chosenOptionsSelector);
	const selectedServices = useSelector(chosenServicesSelector);
	const selectedColor = useSelector(chosenColorSelector);
	const selectedDuration = useSelector(chosenDurationSelector);
	const selectedDurationPrice = useSelector(chosenDurationPriceSelector);
	const selectedKilometrage = useSelector(chosenKilometrageSelector);
	const selectedKilometragePrice = useSelector(chosenPricePointPriceSelector);
	const selectedOrderCode = useSelector(chosenOrderCodeSelector);
	const groupedPricePoints = useSelector(leaseCarGroupedPricePointsSelector);
	const defaultUpgrade = useSelector(defaultUpgradeSelector);
	const paymentInAdvanceChoice = useSelector(paymentInAdvanceChoiceSelector);
	const paymentInAdvanceAmount = useSelector(paymentInAdvanceAmountSelector) ?? 0;
	const isPaymentInAdvanceValid = useSelector(paymentInAdvanceValidSelector);
	const isFlex = useSelector(isFlexSelector);
	const isNewCar = useSelector(isNewCarSelector);
	const dispatch = useDispatch();
	const carImage = useSelector(imageUrlSelector);

	const [conditionalProps, setConditionalProps] = useState({});
	const [receiptProducts, setReceiptProduct] = useState<ReceiptProduct[]>([]);

	const carName = useSelector(leaseCarDataNameSelector);
	const { imageUrl, isLoading } = useImageLoader(carImage, selectedOrderCode);
	const [hasShownSpinnerOnce, setHasShownSpinnerOnce] = useState(false);

	useEffect(() => {
		if (isLoading && !hasShownSpinnerOnce) {
			setHasShownSpinnerOnce(true);
		}
	}, [isLoading, hasShownSpinnerOnce]);

	useEffect(() => {
		const subproductsForUpgrade = selectedUpgrade
			? [
					{
						label: selectedUpgrade.description,
						price: isNewCar ? selectedUpgrade.monthPrice : undefined,
					},
				]
			: [];

		const subproductsForTransmission = isNewCar
			? []
			: [
					{
						label: GearType[defaultUpgrade.vehicleData.gearType as GearTypeKey] || '-',
					},
				];
		const subproductsForOptions = selectedOptions
			? selectedOptions.map((option) => ({
					label: option.description,
					price: option.monthPrice,
				}))
			: [];

		const subproductsForServices = selectedServices
			? selectedServices.map((service) => ({
					label: service.description,
					price: service.price,
				}))
			: [];

		const subproductsForColor = selectedColor
			? [
					{
						label: selectedColor.name,
						price: isNewCar ? selectedColor.price : undefined,
					},
				]
			: [];
		const kilometrageObject =
			groupedPricePoints &&
			selectedDuration &&
			selectedKilometrage &&
			groupedPricePoints[Number(selectedDuration)]
				? groupedPricePoints[Number(selectedDuration)].kilometrages.filter(
						(km) => km.mileage === Number(selectedKilometrage)
					)[0]
				: undefined;

		const carSubProducts = [
			{ label: 'Basisprijs', price: basePrice },
			...subproductsForUpgrade,
			...subproductsForTransmission,
			...subproductsForColor,
			...subproductsForOptions,
			...subproductsForServices,
		];

		const milageSubProducts = [
			{
				label: `Looptijd${isFlex ? ' minimaal' : ''} ${selectedDuration} maanden`,
				price: selectedDurationPrice,
			},
			...(kilometrageObject
				? [
						{
							label: `${formatKilometrageDescription(kilometrageObject.description)} km per jaar`,
							price: selectedKilometragePrice,
						},
					]
				: []),
		];

		const products: ReceiptProduct[] = [
			{
				label: `${carName} (incl. opties)`,
				price: getSubProductsTotalPrice(carSubProducts),
				subproducts: carSubProducts,
			},
			{
				label: 'Looptijd en kilometers',
				price: getSubProductsTotalPrice(milageSubProducts),
				subproducts: milageSubProducts,
			},
		];

		if (paymentInAdvanceChoice && isPaymentInAdvanceValid) {
			products.push({
				label: 'Vooruitbetaling',
				price: -getMonthlyPriceReduction(Number(selectedDuration), paymentInAdvanceAmount),
				subproducts: [
					{
						label: 'Eenmalig bedrag',
						value: `${formatPrice(paymentInAdvanceAmount, {
							abbreviateCents: true,
						})}`,
					},
				],
			});
		}

		setReceiptProduct(products);

		let totalPrice = 0;
		products.forEach((product) => {
			if (product.price && product.label !== 'Vooruitbetaling') {
				// Some sub products don't have a price
				totalPrice += product.price;
			}
		});

		dispatch(setChosenLeaseCarTotalPrice(totalPrice));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		carName,
		basePrice,
		selectedOptions,
		selectedServices,
		selectedDuration,
		selectedDurationPrice,
		groupedPricePoints,
		selectedKilometragePrice,
		selectedKilometrage,
		selectedOrderCode,
		selectedUpgrade,
		selectedColor,
		paymentInAdvanceAmount,
		isPaymentInAdvanceValid,
	]);

	useEffect(() => {
		setConditionalProps({
			alternativeTermLabel: 'ANWB-lidmaatschap per jaar',
			alternativeTermPrice: 17,
		});
	}, [isVerifiedMember]);
	return (
		<>
			<GlobalReceiptStyle strikeThroughMembershipCost={!!isVerifiedMember} />
			<ReceiptContainer id="private-lease-receipt">
				{isLoading && !hasShownSpinnerOnce && <Spinner data-test="receipt-spinner" size="small" />}
				<LegacyReceipt
					dataTest="private-lease-receipt"
					priceDisclaimerIsUSP
					headerImage={{
						src: isNewCar ? imageUrl : carImage,
						alt: carName,
					}}
					products={receiptProducts}
					// @ts-expect-error Receipt doesn't allow an empty price for top-level products
					infoProducts={RECEIPT_USPS}
					loading={false}
					totalLabel="Totaal per maand"
					priceDisclaimer="De aanvraag is <b>vrijblijvend</b> en <b>zonder kosten</b>"
					receiptFooter={
						<ReceiptFooter>
							<Typography variant="support-text" as="div">
								In samenwerking met
							</Typography>
							{AyvensImage}
						</ReceiptFooter>
					}
					{...conditionalProps}
				/>
			</ReceiptContainer>
		</>
	);
};
